import { Player } from '@lottiefiles/react-lottie-player';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Avatar, Badge, Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Stomp } from '@stomp/stompjs';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import SockJS from 'sockjs-client';
import loader from './loader.json';
import { GetRecentMessages, getCommonChatRoom, createChatRoom } from '../../Utils/apiService';
import useStyles from './styles';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import Picker from '@emoji-mart/react';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ChatIcon from '@mui/icons-material/Chat';

interface IChatPayload {
  content?: string;
  senderId?: string;
  chatRoomId?: string;
  timestamp?: string;
  attachmentUrl?: string;
}

interface ChatBoxProps {
  onClose: () => void;
  name?: string;
  avatarImg?: string;
  mentordetails?: {
    userId: string;
    otherUserId: any;
    chatid?: string;
  };
}

const P2PChatBox: React.FC<ChatBoxProps> = ({ onClose, name, avatarImg, mentordetails }) => {
  const classes = useStyles();
  const [chatMessages, setChatMessages] = useState<any>([]);
  const [newMessage, setNewMessage] = useState('');
  const { state: { user } } = useContext(AuthContext);
  const userData = user as any;
  const idTokens = localStorage.getItem('user');
  const idToken = JSON.parse(idTokens || '{}');
  const [stompClient, setStompClient] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [chatRoomId, setChatRoomId] = useState<string | null>(mentordetails?.chatid || null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<{ [key: number]: boolean }>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Suppress ResizeObserver errors
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications')) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    if (!chatRoomId) return;
    
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await GetRecentMessages(chatRoomId, 1, 20);
        setChatMessages(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
      setLoading(false);
    };

    fetchMessages();
  }, [chatRoomId]);

  const getFirstLetters = (str: string) => {
    const words = str.split(' ');
    let firstLetters;
    if (words.length === 1) {
      firstLetters = words.map((word) => word.slice(0, 1));
    } else {
      firstLetters = words.slice(0, 2).map((word) => word.slice(0, 1));
    }
    return firstLetters.join('');
  };

  const formatDate = (timestamp: string) => {
    const messageDate = parseISO(timestamp);

    if (isToday(messageDate)) {
      return 'Today';
    } else if (isYesterday(messageDate)) {
      return 'Yesterday';
    } else {
      return format(messageDate, 'MMMM d, yyyy');
    }
  };

  const groupMessagesByDate = (messages?: IChatPayload[]) => {
    const grouped: { [key: string]: IChatPayload[] } = {};

    messages?.forEach((message) => {
      const timestamp = message?.timestamp;
      if (timestamp) {
        const dateLabel = formatDate(timestamp);

        if (!grouped[dateLabel]) {
          grouped[dateLabel] = [];
        }

        grouped[dateLabel].push(message);
      }
    });

    return grouped;
  };

  // WebSocket connection
  useEffect(() => {
    let socket: any;
    let client: any;
    let reconnectTimeout: any;

    const connectWebSocket = () => {
      socket = new SockJS(`https://turbosend-dev-bam-api.turboverse.co/chat-websocket`);
      client = Stomp.over(socket);

      client.connect(
        {},
        () => {
          setStompClient(client);
          console.log('WebSocket Connected');
        },
        (error: any) => {
          console.log('WebSocket Disconnected, attempting to reconnect...', error);
          reconnectTimeout = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      );
    };

    connectWebSocket();
    
    return () => {
      if (client) {
        client.disconnect(() => console.log('WebSocket Disconnected'));
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, []);

  const subscribeToChatRoom = useCallback(
    (chatRoomId: string) => {
      if (stompClient && stompClient.connected && chatRoomId) {
        const subscription = stompClient.subscribe(`/topic/chatRoom/${chatRoomId}`, (message: any) => {
          const receivedMessage = JSON.parse(message.body);
          setChatMessages((prevMessages: any) => [...prevMessages, receivedMessage]);
        });

        return () => {
          console.log('Unsubscribing from chat room:', chatRoomId);
          subscription.unsubscribe();
        };
      }
      return () => {};
    },
    [stompClient]
  );

  useEffect(() => {
    if (stompClient && chatRoomId) {
      const unsubscribe = subscribeToChatRoom(chatRoomId);
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [stompClient, chatRoomId, subscribeToChatRoom]);

  const getRecentMessages = useCallback(async () => {
    if (!chatRoomId) return;
    
    setIsFetchingMessages(true);
    try {
      const recentMessages = await GetRecentMessages(chatRoomId, page, 20);

      if (recentMessages?.data?.data.length > 0) {
        setChatMessages((prevMessages: any) => {
          const reversedPreviousMessages = [...prevMessages].reverse();
          return [...reversedPreviousMessages, ...recentMessages?.data?.data].reverse();
        });
        setPage((prevPage) => prevPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching recent messages:', error);
    } finally {
      setIsFetchingMessages(false);
    }
  }, [chatRoomId, page]);

  useEffect(() => {
    if (chatRoomId && hasMore && !isFetchingMessages) {
      getRecentMessages();
    }
  }, [chatRoomId, getRecentMessages, hasMore, isFetchingMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Fetch or create chat room for P2P if chatid is not present
  useEffect(() => {
    const fetchOrCreateChatRoom = async () => {
      if (chatRoomId || !mentordetails?.userId || !mentordetails?.otherUserId) return;
      
      try {
        const chatRoom: any = await getCommonChatRoom(mentordetails.userId, mentordetails.otherUserId);
        if (chatRoom?.data?.data?.length === 0) {
          const payload = {
            name: 'p2p chat',
            participantIds: [mentordetails.userId, mentordetails.otherUserId],
            adminId: mentordetails.userId,
          };
          const createRoomResponse = await createChatRoom(payload);
          if (createRoomResponse?.data?.success) {
            setChatRoomId(createRoomResponse.data.data.id);
          }
        } else {
          setChatRoomId(chatRoom.data.data[0]?.id);
        }
      } catch (error) {
        console.error('Chat room error:', error);
      }
    };
    
    fetchOrCreateChatRoom();
  }, [mentordetails, chatRoomId]);

  // Send new message
  const handleSendMessage = async () => {
    setShowEmojiPicker(false);
    if (newMessage.trim() === '' || !stompClient || !chatRoomId || !stompClient.connected) {
      return;
    }

    const messagePayload = {
      content: newMessage.trim(),
      senderId: userData.applicantId,
      chatRoomId: chatRoomId,
      attachmentUrl: null,
      timestamp: new Date().toISOString(),
      userName: userData?.applicantName
    };
    console.log(messagePayload, 'messagePayload23456');
    stompClient.send('/app/sendMessage', { Authorization: `Bearer ${idToken?.turbosendToken?.idToken}` }, JSON.stringify(messagePayload));
    setNewMessage('');
  };

  const handleEmojiSelect = (emoji: { native: string }) => {
    setNewMessage((prev) => prev + emoji.native);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showEmojiPicker && 
          !target.closest('.emoji-picker-container') && 
          !target.closest('.emoji-button')) {
        setShowEmojiPicker(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showEmojiPicker) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showEmojiPicker]);

  // Cleanup emoji picker on unmount
  useEffect(() => {
    return () => {
      setShowEmojiPicker(false);
    };
  }, []);

  // Handle emoji picker positioning to prevent overflow
  useEffect(() => {
    if (showEmojiPicker && emojiPickerRef.current) {
      const picker = emojiPickerRef.current;
      const rect = picker.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Check if picker would overflow to the right
      if (rect.right > viewportWidth - 20) {
        picker.style.left = 'auto';
        picker.style.right = '0';
      } else {
        picker.style.left = '0';
        picker.style.right = 'auto';
      }
    }
  }, [showEmojiPicker]);

  const toggleMessage = (index: number) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleZoomToggle = () => {
    setIsZoomed(!isZoomed);
  };

  // Grouped messages
  const groupedMessages = groupMessagesByDate(chatMessages);
  
  // Custom sorting function to handle date labels properly - like WhatsApp/LinkedIn
  const sortDateKeys = (a: string, b: string) => {
    // Define the order: oldest dates first, "Today" last
    const dateOrder = {
      'Today': 3,
      'Yesterday': 2,
    };
    
    const aOrder = dateOrder[a as keyof typeof dateOrder] || 1;
    const bOrder = dateOrder[b as keyof typeof dateOrder] || 1;
    
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    
    // If both are actual dates (not "Today" or "Yesterday"), sort by date (oldest first)
    if (aOrder === 1 && bOrder === 1) {
      return new Date(a).getTime() - new Date(b).getTime();
    }
    
    return 0;
  };
  
  const sortedDateKeys = Object.keys(groupedMessages).sort(sortDateKeys);
  // Don't reverse - keep the natural order (oldest to newest)

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={isZoomed ? 'xl' : 'sm'}
      fullWidth
      fullScreen={isMobile}
      className={classes.chatBox}
      hideBackdrop
      sx={{
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isMobile ? 0 : '12px',
          height: isMobile ? '100vh' : undefined,
          minHeight: isMobile ? '100vh' : undefined,
          ...(isMobile
            ? {}
            : isZoomed
              ? {
                transform: 'scale(1.1)',
                maxWidth: '700px !important',
                height: '60vh',
                transition: 'all 0.3s ease',
                left: '20% !important',
                top: '13% !important',
              }
              : {
  transform: 'scale(1)',
  maxWidth: '500px !important',
  height: '45vh',
  transition: 'all 0.3s ease',
  position: 'fixed',      
  bottom: -15,         
  right: -20, 
  zIndex: 1300,          
}
),
          '& .MuiDialogActions-root': {
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
          },
        },
        '& .MuiDialogContent-root': {
          overflowY: 'auto',
          marginBottom: isMobile ? 'auto' : '60px',
          padding: '20px',
          flexGrow: 1,
        },
      }}
    >
      <DialogTitle sx={{ padding: '0px', borderRadius: { xs: '9px', ultraHD: '12px' } }}>
        <Box className={classes.headerContaine}>
          <Box display="flex" alignItems="center">
            <Badge
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              overlap="circular"
              variant="dot"
              color="success"
              sx={{ '& .MuiBadge-dot': { border: '1px solid #fff' } }}
            >
              {avatarImg ? (
                <Avatar
                  src={avatarImg}
                  sx={{ width: 40, height: 40 }}
                  alt={name}
                />
              ) : (
                <Avatar
                  sx={{ bgcolor: 'rgba(255, 255, 255, 1)', color: 'rgba(106, 78, 166, 1)' }}
                >
                  {getFirstLetters(name || '')}
                </Avatar>
              )}
            </Badge>

            <Typography className={classes.supportTeamName}>
              {name ? name : 'Support Team'}
            </Typography>
          </Box>

          <Box>
            {!isMobile && (
              <IconButton
                color="primary"
                onClick={handleZoomToggle}
                aria-label={isZoomed ? "zoom out" : "zoom in"}
                className={classes.closeButton}
              >
                {isZoomed ? (
                  <ZoomInMapIcon sx={{ fontSize: { xs: '18px', ultraHD: '24px' } }} />
                ) : (
                  <ZoomOutMapIcon sx={{ fontSize: { xs: '18px', ultraHD: '24px' } }} />
                )}
              </IconButton>
            )}
            <IconButton 
              color="primary" 
              onClick={onClose} 
              aria-label="close" 
              className={classes.closeButton}
            >
              <CloseOutlinedIcon sx={{ fontSize: { xs: '18px', ultraHD: '24px' } }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className={`scrollbar-cus ${classes.chatMessagesContainer}`} ref={messageContainerRef}>
        {loading ? (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center', height: '100%', alignItems: 'center' }}>
            <Player autoplay loop src={loader} speed={2} style={{ height: '150px' }}></Player>
          </Box>
        ) : sortedDateKeys.length === 0 ? (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center', height: '100%', alignItems: 'center' }}>
            <Box textAlign={"center"}>
              <ChatIcon sx={{ color: "#A0B6FF", width: '48px', height: '48px' }} />
              <Typography>Start a conversation</Typography>
            </Box>
          </Box>
        ) : (
          sortedDateKeys.map((date, index) => (
            <React.Fragment key={`date-${index}`}>
              <Box className={classes.dateSeparator}>
                <Box className={classes.date}>{date}</Box>
              </Box>
              {[...groupedMessages[date]].sort(
                (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              ).map((message: any, messageIndex: any) => {
                const messageTime = format(parseISO(message.timestamp), 'hh:mm a');

                return (
                  <React.Fragment key={messageIndex}>
                    <div
                      style={{ fontSize: 10 }}
                      className={message.senderId === userData.applicantId ? classes.chatMessageUsername : classes.chatMessageAdminname}
                    >
                      {message.senderId === userData.applicantId ? 'You' : message?.userName}, {messageTime}
                    </div>
                    <Paper className={message.senderId === userData.applicantId ? classes.chatMessageUser : classes.chatMessageAdmin}>
                      <Typography
                        className={message.senderId === userData.applicantId ? classes.chatMsg : classes.senderMsg}
                        style={{
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {message.content
                          ? message.content.length > 500 && !expandedMessages[messageIndex]
                            ? `${message.content.slice(0, 500)}`
                            : message.content
                          : ''}
                      </Typography>

                      {message.content && message.content.length > 500 && (
                        <Typography
                          onClick={() => toggleMessage(messageIndex)}
                          style={{ 
                            color: '#6A4EA6', 
                            cursor: 'pointer', 
                            fontSize: '10px', 
                            marginTop: '5px', 
                            fontWeight: 500, 
                            width: '80%' 
                          }}
                        >
                          {expandedMessages[messageIndex] ? 'View Less' : 'View More'}
                        </Typography>
                      )}
                    </Paper>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))
        )}
      </DialogContent>

      <DialogActions style={{ display: 'contents' }}>
        <Box className={classes.messageInputContainer} sx={{ position: 'relative' }}>
          <div className={classes.dibtextflex}>
            <TextField
              sx={{
                width: '100%',
                '& .MuiInputBase-input': { color: '#585A64' },
              }}
              className={classes.messageInput}
              placeholder="Type here"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              multiline
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              InputProps={{
                sx: { border: 'none', boxShadow: 'none', borderRadius: '1px' },
                classes: {
                  notchedOutline: classes.notchedOutline,
                },
              }}
            />
          </div>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
            <IconButton 
              onClick={() => setShowEmojiPicker((prev) => !prev)}
              className="emoji-button"
              sx={{ 
                position: 'relative',
                minWidth: '40px',
                height: '40px',
              }}
            >
              <EmojiEmotionsOutlinedIcon />
            </IconButton>
            
            {showEmojiPicker && (
              <Box 
                ref={emojiPickerRef}
                className={`${classes.emojiPickerContainer} emoji-picker-container`}
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  zIndex: 1400,
                  marginBottom: '8px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #e0e0e0',
                  width: '245px',
                  height: '33vh',
                  overflow: 'hidden',
                  transform: 'translateZ(0)',
                  '& .emoji-mart': {
                    border: 'none',
                    borderRadius: '8px',
                    width: '100%',
                  },
                  '& .emoji-mart-scroll': {
                    maxHeight: '250px',
                  },
                  '& .emoji-mart-search': {
                    padding: '8px',
                  },
                  '& .emoji-mart-category-label': {
                    padding: '8px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    width: '247px',
                    height: '320px',
                    left: '-50% !important',
                    right: 'auto !important',
                    transform: 'translateX(-50%) translateZ(0)',
                    '& .emoji-mart-scroll': {
                      maxHeight: '220px',
                    },
                  },
                }}
              >
                <Picker 
                  dynamicWidth={false}
                  perLine={6}
                  onEmojiSelect={handleEmojiSelect}
                  theme="light"
                  set="native"
                  data={async () => {
                    const response = await fetch('https://cdn.jsdelivr.net/npm/@emoji-mart/data@latest/sets/14/native.json');
                    return response.json();
                  }}
                  previewPosition="none"
                  skinTonePosition="none"
                  searchPosition="sticky"
                  maxFrequentRows={0}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            )}
            
            <IconButton 
              color="primary" 
              onClick={handleSendMessage} 
              aria-label="reply" 
              className={classes.replyButton}
              sx={{ 
                minWidth: '40px',
                height: '40px',
              }}
            >
              <SendOutlinedIcon sx={{ color: '#6A4EA6', fontSize: { xs: '18px', ultraHD: '24px' } }} />
            </IconButton>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default P2PChatBox;